<template>
  <div class="copyrights-carousel py-120px bg-white overflow-hidden select-none lt-sm:py-160px">
    <div class="landing-container">
      <div class="text-center mb-80px lt-sm:mb-40px">
        <h2
          class="section-title text-black pf-sc-semibold font-s-36px sm:lh-60px lt-sm:font-s-48px mb-4px"
        >
          软件著作权
        </h2>
        <p class="text-main/70 font-s-18px lt-sm:font-s-26px">
          全栈自研核心系统，拥有完全自主知识产权
        </p>
      </div>
    </div>

    <div class="landing-container w-full mx-auto relative">
      <div
        ref="carouselRef"
        class="carousel-container relative h-420px perspective-1000px flex items-center justify-center lt-sm:h-360px"
        @click="handleContainerClick"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
      >
        <!-- Navigation Buttons (Desktop) -->
        <button
          class="nav-btn wh-40px flex-center prev absolute left-0% z-110 p-12px rounded-full bg-white shadow-lg hover:bg-slate-50 transition-all duration-300 lt-sm:hidden group border border-slate-100"
          aria-label="上一个著作权"
          @click="prev"
        >
          <i
            class="i-ep-arrow-left font-s-20px text-slate-400 group-hover:text-blue-600"
            aria-hidden="true"
          ></i>
        </button>
        <button
          class="nav-btn wh-40px flex-center next absolute right-0% z-110 p-12px rounded-full bg-white shadow-lg hover:bg-slate-50 transition-all duration-300 lt-sm:hidden group border border-slate-100"
          aria-label="下一个著作权"
          @click="next"
        >
          <i
            class="i-ep-arrow-right font-s-20px text-slate-400 group-hover:text-blue-600"
            aria-hidden="true"
          ></i>
        </button>

        <div
          class="carousel-track relative w-full h-full flex items-center justify-center transform-style-3d"
        >
          <div
            v-for="item in visibleItems"
            :key="item.uniqueKey"
            class="carousel-item absolute transition-all duration-700 cubic-bezier(0.25, 0.8, 0.25, 1) shadow-xl bg-white border border-slate-100 rounded-8px overflow-hidden will-change-transform focus-visible:outline-primary"
            :class="{
              'z-10': item.offset === 0,
              'shadow-2xl shadow-blue-500/20': item.offset === 0,
              'cursor-pointer': item.offset !== 0,
              'cursor-default': item.offset === 0,
              'pointer-events-none': !isMobile && item.offset === 0,
            }"
            :style="getItemStyle(item.offset)"
            :data-offset="item.offset"
            :aria-label="`查看 ${item.data?.title} 证书`"
            tabindex="0"
            @click="handleClick(item.offset)"
            @keydown.enter="handleClick(item.offset)"
            @keydown.space.prevent="handleClick(item.offset)"
          >
            <div
              class="absolute inset-0 bg-slate-900/10 pointer-events-none transition-opacity duration-500"
              :style="{
                opacity: item.offset === 0 ? 0 : Math.min(0.5, Math.abs(item.offset) * 0.1),
              }"
            ></div>

            <div
              class="w-260px h-360px p-10px flex flex-col items-center justify-center lt-sm:w-220px lt-sm:h-300px bg-white"
            >
              <!-- Certificate Image -->
              <div
                class="w-full h-full relative overflow-hidden rounded-4px border border-slate-100"
              >
                <img
                  v-if="item.data?.image"
                  :src="item.data.image"
                  class="w-full h-full object-cover"
                  :alt="item.data?.title"
                />
                <div
                  class="absolute bottom-0 left-0 w-full bg-white/90 p-8px lt-sm:p-12px text-center border-t border-slate-100"
                >
                  <h3 class="font-s-12px font-bold text-slate-800 line-clamp-2 lt-sm:font-s-18px">
                    {{ item.data?.title }}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { softwareCopyrights } from '@/constants/patents'

  const activeIndex = ref(0)
  const totalItems = softwareCopyrights.length
  const carouselRef = ref<HTMLElement | null>(null)

  // Show a window of items around the active index
  const visibleCount = 15 // Increased to ensure smooth transition for 7 items
  const centerIndex = Math.floor(visibleCount / 2)

  const visibleItems = computed(() => {
    const items = []
    if (totalItems === 0) return []
    for (let i = -centerIndex; i <= centerIndex; i++) {
      let idx = (activeIndex.value + i) % totalItems
      if (idx < 0) idx += totalItems
      if (softwareCopyrights[idx]) {
        items.push({
          originalIndex: idx,
          offset: i,
          data: softwareCopyrights[idx],
          uniqueKey: `item-${idx}`,
        })
      }
    }
    return items
  })

  const getItemStyle = (offset: number) => {
    const absOffset = Math.abs(offset)

    // Config based on screen size
    const xOffset = isMobile.value ? 100 : 230
    const rotateY = isMobile.value ? 15 : 15

    let transform = ''
    let opacity = 1
    let zIndex = 100 - absOffset

    if (offset === 0) {
      // 降低 translateZ 值，确保投影后的视觉高度不超过容器高度 420px
      // 计算：360px * (1000 / (1000 - 120)) ≈ 409px
      transform = `translateX(0) translateZ(${isMobile.value ? 80 : 120}px) rotateY(0) scale(1)`
      opacity = 1
      zIndex = 100
    } else {
      const sign = Math.sign(offset)
      // 保持合理的间距
      const step = isMobile.value ? 30 : 210
      const x = sign * (xOffset + (absOffset - 1) * step)

      // 相应调整背景项的 Z 轴位移
      const z = -absOffset * (isMobile.value ? 100 : 120)
      const r = -sign * rotateY

      transform = `translateX(${x}px) translateZ(${z}px) rotateY(${r}deg) scale(${
        1 - absOffset * 0.05
      })`

      // Ensure 7 items (offset <= 3) are clearly visible
      opacity = absOffset <= 3 ? 1 : Math.max(0, 1 - (absOffset - 2) * 0.3)
    }

    return {
      transform,
      opacity,
      zIndex,
      transitionTimingFunction: 'cubic-bezier(0.25, 0.8, 0.25, 1)', // Ensure smooth movement
    }
  }

  const prev = () => {
    activeIndex.value = (activeIndex.value - 1 + totalItems) % totalItems
  }

  const next = () => {
    activeIndex.value = (activeIndex.value + 1) % totalItems
  }

  const handleClick = (offset: number) => {
    if (offset === 0) return
    if (offset < 0) {
      prev()
    } else {
      next()
    }
  }

  const handleContainerClick = (e: MouseEvent) => {
    if (isMobile.value) return
    const target = e.target as HTMLElement | null
    if (target?.closest('.nav-btn')) return
    const container = carouselRef.value
    if (!container) return
    const centerEl = container.querySelector('[data-offset="0"]') as HTMLElement | null
    if (centerEl) {
      const rect = centerEl.getBoundingClientRect()
      if (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      ) {
        return
      }
    }
    const containerRect = container.getBoundingClientRect()
    const centerX = containerRect.left + containerRect.width / 2
    if (e.clientX < centerX) {
      prev()
    } else {
      next()
    }
  }

  // Touch Handling
  let touchStartX = 0
  let touchEndX = 0

  const onTouchStart = (e: TouchEvent) => {
    if (e.changedTouches.length > 0 && e.changedTouches[0]) {
      touchStartX = e.changedTouches[0].screenX
    }
  }

  const onTouchMove = (e: TouchEvent) => {
    if (e.changedTouches.length > 0 && e.changedTouches[0]) {
      touchEndX = e.changedTouches[0].screenX
    }
  }

  const onTouchEnd = () => {
    if (touchStartX - touchEndX > 50) {
      next()
    } else if (touchEndX - touchStartX > 50) {
      prev()
    }
  }

  const isMobile = ref(false)

  onMounted(() => {
    isMobile.value = window.innerWidth < 640
    window.addEventListener('resize', () => {
      isMobile.value = window.innerWidth < 640
    })
  })
</script>

<style scoped>
  .copyrights-carousel {
    @apply bg-cover bg-center bg-no-repeat;
    background-image: url('@/assets/images/bg-ability.png');
  }

  .perspective-1000px {
    perspective: 1000px;
  }
  .transform-style-3d {
    transform-style: preserve-3d;
  }
</style>
